import { homeBrokerageStrip } from '@/config/marketing-brokerages.config'
import { homePressLogos } from '@/config/marketing-press.config'
import { getAppUrl } from '@/config/marketing-nav.config'
import { meetIvyTestimonials } from '@/config/marketing-content/testimonials'
import type { FeatureSpotlightItem } from '@/components/marketing/feature-spotlight'

const signupHref = getAppUrl('/signup')

export const meetIvyContent = {
  slug: 'meet-ivy',
  eyebrow: 'Try Ivy — the Portfolio-Aware AI Trading Assistant',
  heroTitle: 'Never Trade Alone Again',
  heroLead:
    'Ivy is the intelligence layer that brings your portfolio, market data, charts, scans, options, and research agents together into one AI-native trading interface.',
  asSeenOnTitle: 'As Seen On',
  asSeenOnLogos: homePressLogos,
  brokerageTitle: 'Major Brokerages Supported',
  brokerageBlurb:
    'Connect your brokerage to Ivy and join hundreds of millions in linked assets. Your portfolio conversation starts here.',
  brokerages: homeBrokerageStrip,
  featureSpotlights: [
    {
      kicker: 'Portfolio Intelligence',
      title: 'Bring your portfolio into the conversation',
      lead:
        'Ivy connects with your brokerage accounts and uses custom risk profile calibration to make answers more relevant to your portfolio. Ivy can understand what you own, what changed, where your risk lives, and how market events may affect your positions.',
      imageSlug: 'portfolio-intelligence',
      imageAlt: 'Portfolio Intelligence product screenshot',
      ctaLabel: 'Get Started',
      ctaHref: signupHref,
      imageSide: 'right',
    },
    {
      kicker: 'Market Intelligence',
      title: 'Turn market noise into portfolio signals',
      lead:
        'Ask Ivy anything and move from live market data to portfolio-relevant answers, charts, scans, options chains, and watchlist signals - all in one thread.',
      imageSlug: 'market-intelligence',
      imageAlt: 'Market Intelligence product screenshot',
      ctaLabel: 'Get Started',
      ctaHref: signupHref,
      imageSide: 'left',
    },
    {
      kicker: 'Research Intelligence',
      title: 'Your hyper-specialized research desk',
      lead:
        'Ivy Pro gives you specialized AI research agents built for deeper market analysis. Each agent runs a focused workflow, so you can move beyond generic chat and get structured answers needed for active trading.',
      imageSlug: 'research-intelligence',
      imageAlt: 'Research Intelligence product screenshot',
      ctaLabel: 'Get Started',
      ctaHref: signupHref,
      imageSide: 'right',
    },
    {
      kicker: 'Ivy Baskets',
      title: 'Test the thesis before you touch the trade',
      lead:
        'Tell Ivy the idea — a theme, a ticker list, or a portfolio you saw somewhere else — and Ivy builds it into a simulated basket with a starting balance and live tracking. No trades placed. No risk to your brokerage account. Just the question that matters: does your thinking hold up?',
      imageSlug: 'ivy-baskets',
      imageAlt: 'Ivy Baskets simulated portfolio with live performance tracking',
      ctaLabel: 'Get Started',
      ctaHref: signupHref,
      imageSide: 'left',
      prompts: [
        {
          before: '“',
          accent: 'Create a $10,000 AI Infrastructure basket',
          after: ' with NVDA, AVGO, AMD, and VRT.”',
        },
        {
          before: '“Create a ',
          accent: '$1 million basket of leading semiconductor companies',
          after: ' and track it for me.”',
        },
        {
          before: '“',
          accent: 'Track the portfolio pasted below',
          after: ' to create a custom Ivy Basket.”',
        },
      ],
    },
  ] satisfies FeatureSpotlightItem[],
  testimonialTitle: 'Powered by AI, Shaped by Traders',
  testimonials: meetIvyTestimonials,
  ctaTitle: 'The future of trading feels human again',
  ctaLead:
    'Stop trading through disconnected tools and build your own financial Jarvis.',
  ctaPrimaryLabel: 'Get Started',
  ctaPrimaryHref: signupHref,
}

export { pricingContent } from '@/config/marketing-content/pricing'

export { foundingTradersContent } from '@/config/marketing-content/founding-traders'

export { aboutContent } from '@/config/marketing-content/about'

export const supportContent = {
  eyebrow: 'Support',
  heroTitle: 'We’re here to help',
  heroLead: 'Get answers, get unblocked, or email us, whichever you need.',
  cards: [
    {
      title: 'Help center',
      description:
        'Browse guides on connecting brokerages, scans, options, and Ivy workflows.',
      href: 'https://help.podiummarkets.ai/',
      linkLabel: 'Visit the Help Center →',
    },
  ],
}
